/**
 * Tooltip instantáneo para los gráficos SVG (Donut/Bar/MultiBar).
 * Reemplaza al <title> nativo (que aparece con ~1s de retardo y es fácil de no ver):
 * se posiciona sobre el cursor y muestra el dato en cuanto pasas por encima.
 *
 * Uso:
 *   const { containerRef, tip, show, hide } = useChartTooltip()
 *   <div ref={containerRef} className="relative" onMouseLeave={hide}>
 *     <svg>… <rect onMouseMove={(e) => show(e, { title, rows: [...] })} /> …</svg>
 *     <ChartTooltip tip={tip} />
 *   </div>
 */
import { useRef, useState } from 'react'

export interface ChartTooltipRow {
  label?: string
  value: string
  color?: string
}
export interface ChartTooltipData {
  title: string
  rows: ChartTooltipRow[]
}
interface TipState {
  data: ChartTooltipData
  x: number
  y: number
}

export function useChartTooltip() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [tip, setTip] = useState<TipState | null>(null)

  const show = (e: { clientX: number; clientY: number }, data: ChartTooltipData) => {
    const r = containerRef.current?.getBoundingClientRect()
    if (!r) return
    setTip({ data, x: e.clientX - r.left, y: e.clientY - r.top })
  }
  const hide = () => setTip(null)

  return { containerRef, tip, show, hide }
}

export function ChartTooltip({ tip }: { tip: TipState | null }) {
  if (!tip) return null
  // Si el cursor está muy arriba, mostramos el tooltip debajo para no salir del panel.
  const flipDown = tip.y < 60
  return (
    <div
      className="pointer-events-none absolute z-30 rounded-md px-2.5 py-1.5 shadow-xl"
      style={{
        left: tip.x,
        top: tip.y,
        transform: flipDown
          ? 'translate(-50%, 14px)'
          : 'translate(-50%, calc(-100% - 14px))',
        background: 'var(--bg-surface-strong)',
        border: '1px solid var(--border-strong)',
        maxWidth: 240,
      }}
    >
      <div className="text-[11px] font-semibold t-primary whitespace-nowrap overflow-hidden text-ellipsis">
        {tip.data.title}
      </div>
      {tip.data.rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2 text-[11px] whitespace-nowrap mt-0.5">
          {row.color && (
            <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: row.color }} />
          )}
          {row.label && <span className="t-muted">{row.label}</span>}
          <span className="t-secondary font-mono ml-auto pl-3">{row.value}</span>
        </div>
      ))}
    </div>
  )
}
